import { z } from "zod";
import type { ChartConfig } from "@/components/ui/ChartTooltip";

export type ColorUtility =
  | "bg"
  | "stroke"
  | "fill"
  | "text"
  | "from"
  | "to"
  | "inactive";

export type ChartColorObject = {
  active: string | AvailableChartColorsKeys;
  inactive: string | AvailableChartColorsKeys;
};

export const isChartColorObject = (
  color: unknown,
): color is ChartColorObject => {
  return (
    typeof color === "object" &&
    color !== null &&
    "active" in color &&
    "inactive" in color
  );
};

export const chartColors = {
  blue: {
    bg: "bg-blue-500",
    stroke: "stroke-blue-500",
    fill: "fill-blue-500",
    text: "text-blue-500",
    from: "from-blue-400",
    to: "to-blue-600",
    inactive: "fill-blue-200",
  },
  orange: {
    bg: "bg-orange-500",
    stroke: "stroke-orange-500",
    fill: "fill-orange-500",
    text: "text-orange-500",
    from: "from-orange-400",
    to: "to-orange-600",
    inactive: "fill-orange-200",
  },
  violet: {
    bg: "bg-violet-700",
    stroke: "stroke-violet-700",
    fill: "fill-violet-700",
    text: "text-violet-700",
    from: "from-violet-600",
    to: "to-violet-700",
    inactive: "fill-violet-200",
  },
  green: {
    bg: "bg-green-700",
    stroke: "stroke-green-700",
    fill: "fill-green-700",
    text: "text-green-700",
    from: "from-green-400",
    to: "to-green-600",
    inactive: "fill-green-200",
  },
  gray: {
    bg: "bg-gray-300",
    stroke: "stroke-gray-300",
    fill: "fill-gray-300",
    text: "text-gray-300",
    from: "from-gray-400",
    to: "to-gray-600",
    inactive: "fill-gray-200",
  },
  teal: {
    bg: "bg-teal-500",
    stroke: "stroke-teal-500",
    fill: "fill-teal-500",
    text: "text-teal-500",
    from: "from-teal-400",
    to: "to-teal-600",
    inactive: "fill-teal-200",
  },
  rose: {
    bg: "bg-rose-500",
    stroke: "stroke-rose-500",
    fill: "fill-rose-500",
    text: "text-rose-500",
    from: "from-rose-400",
    to: "to-rose-600",
    inactive: "fill-rose-200",
  },
  yellow: {
    bg: "bg-yellow-500",
    stroke: "stroke-yellow-500",
    fill: "fill-yellow-500",
    text: "text-yellow-500",
    from: "from-yellow-400",
    to: "to-yellow-600",
    inactive: "fill-yellow-200",
  },
  fuchsia: {
    bg: "bg-fuchsia-500",
    stroke: "stroke-fuchsia-500",
    fill: "fill-fuchsia-500",
    text: "text-fuchsia-500",
    from: "from-fuchsia-400",
    to: "to-fuchsia-600",
    inactive: "fill-fuchsia-200",
  },
} as const satisfies {
  [color: string]: {
    [key in ColorUtility]: string;
  };
};

/** Resolved color for SVG stroke/fill (mid-tone); gradients use `stops`. */
export const chartGradientColors = {
  blue: {
    id: "blueGradient",
    stroke: "oklch(55% 0.2 257.6)",
    stops: [
      { offset: "0%", color: "oklch(97% 0.0152 257.6)" },
      { offset: "100%", color: "oklch(40.75% 0.184 257.6)" },
    ],
  },
  orange: {
    id: "orangeGradient",
    stroke: "oklch(63% 0.2 41.49)",
    stops: [
      { offset: "0%", color: "oklch(63.05% 0.2 41.49)" },
      { offset: "100%", color: "oklch(27% 0.0886 41.49)" },
    ],
  },
  violet: {
    id: "violetGradient",
    stroke: "oklch(58% 0.245 293)",
    stops: [
      { offset: "0%", color: "oklch(61% 0.245 293.03)" },
      { offset: "100%", color: "oklch(54.25% 0.245 293.02)" },
    ],
  },
  green: {
    id: "greenGradient",
    stroke: "oklch(52% 0.14 149)",
    stops: [
      { offset: "0%", color: "oklch(97% 0.049 149.18)" },
      { offset: "100%", color: "oklch(41.75% 0.1251 149.18)" },
    ],
  },
  gray: {
    id: "grayGradient",
    stroke: "oklch(55% 0.01 286)",
    stops: [
      { offset: "0%", color: "oklch(98% 0.0067 286.07)" },
      { offset: "100%", color: "oklch(21% 0.0067 286.07)" },
    ],
  },
  teal: {
    id: "tealGradient",
    stroke: "oklch(55% 0.12 211)",
    stops: [
      { offset: "0%", color: "oklch(97% 0.0277 210.83)" },
      { offset: "100%", color: "oklch(27% 0.062 210.83)" },
    ],
  },
  rose: {
    id: "roseGradient",
    stroke: "oklch(58% 0.22 27)",
    stops: [
      { offset: "0%", color: "oklch(97% 0.0186 27.16)" },
      { offset: "100%", color: "oklch(27% 0.1237 27.16)" },
    ],
  },
  yellow: {
    id: "yellowGradient",
    stroke: "oklch(75% 0.15 89)",
    stops: [
      { offset: "0%", color: "oklch(97% 0.0442 89.137)" },
      { offset: "100%", color: "oklch(27% 0.0635 89.137)" },
    ],
  },
  fuchsia: {
    id: "fuchsiaGradient",
    stroke: "oklch(58% 0.25 317)",
    stops: [
      { offset: "0%", color: "oklch(97% 0.0234 317.04)" },
      { offset: "100%", color: "oklch(27% 0.1409 317.04)" },
    ],
  },
};

export type AvailableChartGradientsKeys = keyof typeof chartGradientColors;
export type BuiltInChartColor = keyof typeof chartColors;
export type AvailableChartColorsKeys = BuiltInChartColor | `chart-${string}`;
export type ChartColorInput = AvailableChartColorsKeys | ChartColorObject;

export const AvailableChartColors: AvailableChartColorsKeys[] = Object.keys(
  chartColors,
) as Array<AvailableChartColorsKeys>;

export const getColorClassName = (
  colorInput: ChartColorInput,
  type: ColorUtility,
  mode: "solid" | "gradient" = "solid",
): string => {
  // Handle custom color object
  if (isChartColorObject(colorInput)) {
    let color: string;
    if (type === "inactive") {
      // Handle inactive color
      const inactiveColor = colorInput.inactive;
      // Check if inactive color is a built-in color
      const builtInInactive = chartColors[inactiveColor as BuiltInChartColor];
      color = builtInInactive ? builtInInactive.inactive : inactiveColor;
    } else {
      // Handle active color
      const activeColor = colorInput.active;
      // Check if active color is a built-in color
      const builtInActive = chartColors[activeColor as BuiltInChartColor];
      if (builtInActive) {
        if (mode === "gradient") {
          return `${builtInActive.from} ${builtInActive.to} bg-linear-to-br`;
        }
        return builtInActive[type];
      }
      color = activeColor;
    }
    return getUtilityClassName(type, color);
  }

  // Rest of the existing code remains the same
  const builtIn = chartColors[colorInput as BuiltInChartColor];
  if (builtIn) {
    if (mode === "gradient") {
      return `${builtIn.from} ${builtIn.to} bg-linear-to-br`;
    }
    return builtIn[type];
  }

  if (mode === "gradient") {
    return `from-${colorInput} to-${colorInput} bg-linear-to-br`;
  }

  return getUtilityClassName(type, colorInput);
};

const getUtilityClassName = (type: ColorUtility, color: string): string => {
  switch (type) {
    case "bg":
      return `bg-${color}`;
    case "stroke":
      return `stroke-${color}`;
    case "fill":
      return `fill-${color}`;
    case "text":
      return `text-${color}`;
    case "from":
      return `from-${color}`;
    case "to":
      return `to-${color}`;
    case "inactive":
      return `fill-${color}`;
    default:
      return "bg-gray-300";
  }
};

export const getGradientId = (colorKey: ChartColorInput): string => {
  if (typeof colorKey === "string" && colorKey in chartGradientColors) {
    return chartGradientColors[colorKey as AvailableChartGradientsKeys].id;
  }
  return "grayGradient";
};

/** Resolved CSS color for SVG (stroke, gradient stops). Uses chart palette; safe for Recharts/SVG. */
export const getChartColorForSvg = (colorKey: ChartColorInput): string => {
  const key =
    typeof colorKey === "string"
      ? colorKey
      : isChartColorObject(colorKey)
        ? colorKey.active
        : "gray";
  const defaultColor =
    chartGradientColors.gray.stops[chartGradientColors.gray.stops.length - 1]
      ?.color ?? "oklch(21% 0.0067 286.07)";
  if (typeof key === "string" && key in chartGradientColors) {
    const k = key as AvailableChartGradientsKeys;
    return chartGradientColors[k].stroke ?? defaultColor;
  }
  return defaultColor;
};

export const getActiveClass = (colorInput: ChartColorInput): string => {
  if (isChartColorObject(colorInput)) {
    const activeColor = colorInput.active;
    const builtInActive = chartColors[activeColor as BuiltInChartColor];
    if (builtInActive) {
      return builtInActive.fill;
    }
    return `fill-${activeColor}`;
  }
  return getColorClassName(colorInput, "fill");
};

export const getInactiveClass = (colorInput: ChartColorInput): string => {
  if (isChartColorObject(colorInput)) {
    const inactiveColor = colorInput.inactive;
    const builtInInactive = chartColors[inactiveColor as BuiltInChartColor];
    if (builtInInactive) {
      return builtInInactive.inactive;
    }
    return `fill-${inactiveColor}`;
  }

  const builtIn = chartColors[colorInput as BuiltInChartColor];
  if (builtIn) {
    return builtIn.inactive;
  }

  return `fill-${colorInput}`;
};

export const constructCategoryColors = (
  categories: string[],
  colors: ChartColorInput[],
): Map<string, ChartColorInput> => {
  const categoryColors = new Map<string, ChartColorInput>();
  categories.forEach((category, index) => {
    categoryColors.set(category, colors[index % colors.length] ?? "gray");
  });
  return categoryColors;
};

export const getYAxisDomain = (
  autoMinValue: boolean,
  minValue: number | undefined,
  maxValue: number | undefined,
) => {
  const minDomain = autoMinValue ? "auto" : (minValue ?? 0);
  const maxDomain = maxValue ?? "auto";
  return [minDomain, maxDomain];
};

// Tremor Raw hasOnlyOneValueForKey [v0.1.0]

export function hasOnlyOneValueForKey(
  // biome-ignore lint/suspicious/noExplicitAny: <function needs to work with any object type>
  array: any[],
  keyToCheck: string,
): boolean {
  // biome-ignore lint/suspicious/noExplicitAny: <values can be of any type>
  const val: any[] = [];

  for (const obj of array) {
    if (Object.hasOwn(obj, keyToCheck)) {
      val.push(obj[keyToCheck]);
      if (val.length > 1) {
        return false;
      }
    }
  }

  return true;
}

export const getChartRowField = (row: object, key: string): unknown =>
  Reflect.get(row, key);

/** Parses numeric series values from chart rows for the given keys. */
export const getNumericValuesFromChartData = (
  data: unknown[],
  valueKeys: string[],
): number[] => {
  const values: number[] = [];
  for (const row of data) {
    if (typeof row !== "object" || row === null) continue;
    for (const key of valueKeys) {
      const raw = getChartRowField(row, key);
      const n = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isNaN(n)) values.push(n);
    }
  }
  return values;
};

/**
 * Y-axis domain for line/area series: floor at 0 when all values are non-negative;
 * use auto min when any value is negative so negative ticks render.
 */
export const getYAxisDomainForNumericValues = (
  values: number[],
  { autoMinValue = true }: { autoMinValue?: boolean } = {},
) => {
  if (values.length === 0) {
    return getYAxisDomain(autoMinValue, undefined, undefined);
  }

  const min = Math.min(...values);
  if (min < 0) {
    return getYAxisDomain(true, undefined, undefined);
  }

  return getYAxisDomain(false, 0, undefined);
};

export const getYAxisDomainForSeriesData = (
  data: unknown[],
  valueKeys: string[],
  options: { autoMinValue?: boolean } = {},
) => {
  if (valueKeys.length === 0) {
    return getYAxisDomain(options.autoMinValue ?? true, undefined, undefined);
  }

  return getYAxisDomainForNumericValues(
    getNumericValuesFromChartData(data, valueKeys),
    options,
  );
};

/**
 * Stable key for controlled bar selection so parent re-renders with a new object
 * reference do not re-sync local chart state when the selection is unchanged.
 * Uses JSON so `|` in category or row fields cannot collide with the delimiter.
 */
export const getActiveBarSyncKey = (
  bar: { category: string; value: number } | null | undefined,
  rowKey: string,
): string => {
  if (bar == null) return "__none__";
  const rowId = getChartRowField(bar, rowKey);
  return JSON.stringify([bar.category, rowId, bar.value]);
};

/** `#RGB` or `#RRGGBB` only — rejects CSS injection vectors (e.g. `url()`, `var()`). */
const CHART_HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

/** Zod schema for chart fill colours supplied in Layout Studio specs. */
export const chartHexColorSchema = z
  .string()
  .regex(CHART_HEX_COLOR_PATTERN, "Expected #RGB or #RRGGBB hex colour");

/** Runtime guard matching {@link chartHexColorSchema}. */
export const isChartHexColor = (value: unknown): value is string =>
  typeof value === "string" && CHART_HEX_COLOR_PATTERN.test(value);

export type ChartSegmentRow = Record<string, string | number | undefined>;

export interface ResolveCustomSegmentChartConfigInput {
  baseConfig: ChartConfig;
  segmentColors: Record<string, string> | null | undefined;
  data: ChartSegmentRow[];
  /** Category / segment field (e.g. bar `xAxisKey`, pie `nameKey`). */
  segmentKey: string;
  /** Series key used for single-brand tooltip swatches (e.g. `yKey`). */
  defaultSeriesKey: string;
}

export interface ResolveCustomSegmentChartConfigResult {
  resolvedChartConfig: ChartConfig;
  segmentLegendConfig: ChartConfig;
  hasSegmentLegend: boolean;
  legendItems: { key: string; label: string }[];
  hasCustomSegmentColors: boolean;
}

/** Distinct hex values in a segment colour map. */
export const getUniqueSegmentColorValues = (
  segmentColors: Record<string, string>,
): string[] => [...new Set(Object.values(segmentColors))];

/**
 * Legend rows for per-segment colours, in first-seen data order.
 * Skips rows whose segment is missing from `segmentColors`.
 */
export const buildSegmentLegendItems = (
  data: ChartSegmentRow[],
  segmentKey: string,
  segmentColors: Record<string, string>,
): { key: string; label: string }[] => {
  const seen = new Set<string>();
  const items: { key: string; label: string }[] = [];
  for (const row of data) {
    const segment = String(row[segmentKey] ?? "");
    if (segment === "" || !Object.hasOwn(segmentColors, segment)) continue;
    if (seen.has(segment)) continue;
    seen.add(segment);
    items.push({ key: segment, label: segment });
  }
  return items;
};

/** {@link ChartConfig} for per-segment legend and tooltip swatches. */
export const buildSegmentLegendConfig = (
  legendItems: { key: string; label: string }[],
  segmentColors: Record<string, string>,
  fallbackColor = "var(--chart-1)",
): ChartConfig => {
  const config: ChartConfig = {};
  for (const { key, label } of legendItems) {
    config[key] = {
      label: label ?? key,
      color: segmentColors[key] ?? fallbackColor,
    };
  }
  return config;
};

/**
 * Merges optional per-segment hex colours into {@link ChartConfig} for legend and tooltips.
 * One shared hex → updates `defaultSeriesKey`; distinct hexes → per-segment config keys.
 */
export const resolveCustomSegmentChartConfig = ({
  baseConfig,
  segmentColors,
  data,
  segmentKey,
  defaultSeriesKey,
}: ResolveCustomSegmentChartConfigInput): ResolveCustomSegmentChartConfigResult => {
  const hasCustomSegmentColors =
    segmentColors != null && Object.keys(segmentColors).length > 0;

  if (!hasCustomSegmentColors || segmentColors == null) {
    return {
      resolvedChartConfig: baseConfig,
      segmentLegendConfig: {},
      hasSegmentLegend: false,
      legendItems: [],
      hasCustomSegmentColors: false,
    };
  }

  const uniqueColorValues = getUniqueSegmentColorValues(segmentColors);
  const hasSegmentLegend = uniqueColorValues.length > 1;
  const legendItems = hasSegmentLegend
    ? buildSegmentLegendItems(data, segmentKey, segmentColors)
    : [];
  const segmentLegendConfig = hasSegmentLegend
    ? buildSegmentLegendConfig(legendItems, segmentColors)
    : {};

  let resolvedChartConfig: ChartConfig;
  if (!hasSegmentLegend) {
    const brandColor = uniqueColorValues[0];
    resolvedChartConfig =
      brandColor == null
        ? baseConfig
        : {
            ...baseConfig,
            [defaultSeriesKey]: {
              label: baseConfig[defaultSeriesKey]?.label,
              color: brandColor,
            },
          };
  } else {
    resolvedChartConfig = { ...baseConfig, ...segmentLegendConfig };
  }

  return {
    resolvedChartConfig,
    segmentLegendConfig,
    hasSegmentLegend,
    legendItems,
    hasCustomSegmentColors: true,
  };
};
